
import { GoogleGenAI, Modality } from "@google/genai";
import { CONFIG } from "../../lib/config";

// Initialize AI directly in the frontend using the guaranteed process.env.API_KEY
// Note: We create a fresh instance or use a singleton. 
// As per guidelines, we should use process.env.API_KEY directly.
const getAI = () => new GoogleGenAI({ apiKey: CONFIG.API_KEY || (typeof process !== 'undefined' ? process.env.API_KEY : '') });

export const decodeBase64 = (base64: string) => {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

// Helper to wrap PCM data into a playable WAV
export const addWavHeader = (pcmData: ArrayBuffer, sampleRate = 24000, numChannels = 1) => {
  const byteLength = pcmData.byteLength;
  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  const writeString = (view: DataView, offset: number, string: string) => { 
    for (let i = 0; i < string.length; i++) view.setUint8(offset + i, string.charCodeAt(i)); 
  };
  writeString(view, 0, 'RIFF'); 
  view.setUint32(4, 36 + byteLength, true); 
  writeString(view, 8, 'WAVE'); 
  writeString(view, 12, 'fmt '); 
  view.setUint32(16, 16, true); 
  view.setUint16(20, 1, true); 
  view.setUint16(22, numChannels, true); 
  view.setUint32(24, sampleRate, true); 
  view.setUint32(28, sampleRate * numChannels * 2, true); 
  view.setUint16(32, numChannels * 2, true); 
  view.setUint16(34, 16, true); 
  writeString(view, 36, 'data'); 
  view.setUint32(40, byteLength, true);
  return new Blob([header, pcmData], { type: 'audio/wav' });
};

export function tryParsePartialJSON(jsonString: string): any | null {
  if (!jsonString) return null;
  let clean = jsonString.trim();
  clean = clean.replace(/```json/gi, "").replace(/```/g, "");
  
  const firstBrace = clean.indexOf('{');
  const lastBrace = clean.lastIndexOf('}');
  if (firstBrace === -1) return null;

  let candidate = clean.substring(firstBrace);
  if (lastBrace !== -1 && lastBrace > firstBrace) {
    candidate = clean.substring(firstBrace, lastBrace + 1);
  }

  try {
    return JSON.parse(candidate);
  } catch (e) {
    const repairs = ['"}', '"]}', ']}', '}', '"]', ']', '""}', 'null}', '0}'];
    for (const repair of repairs) {
      try { return JSON.parse(candidate + repair); } catch (e2) {}
    }
    return null;
  }
}

/**
 * Directly invoke Gemini from Frontend
 */
export async function invokeAI(action: string, payload: any) {
  const ai = getAI();
  const modelName = action === 'generate-tts' ? 'gemini-2.5-flash-preview-tts' : 'gemini-3-flash-preview';

  if (action === 'generate-tts') {
    const response = await ai.models.generateContent({
      model: modelName,
      contents: [{ parts: [{ text: payload.text }] }],
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } } },
      },
    });
    return { audioContent: response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data };
  }

  // Handle other actions by constructing appropriate prompts
  const systemInstruction = `You are a helpful AI tutor in a game called WonderVerse. 
  Action: ${action}. 
  User Profile: Grade ${payload.grade}, Interests: ${payload.interests?.join(',')}.
  Always respond in JSON format if requested.`;

  const response = await ai.models.generateContent({
    model: 'gemini-3-flash-preview',
    contents: JSON.stringify(payload),
    config: {
      systemInstruction,
      responseMimeType: "application/json"
    }
  });

  return tryParsePartialJSON(response.text || '');
}

/**
 * Directly stream Gemini from Frontend
 */
export async function streamAI(action: string, payload: any, onChunk: (data: string) => void) {
  const ai = getAI();
  
  const systemInstruction = `You are the WonderVerse AI Engine. Generate a lesson for a Grade ${payload.grade} student. 
  Topic: ${payload.topic}. Lang: ${payload.lang}.
  Response must be a SINGLE JSON object of LessonContent.
  Widgets: visual_image, comic_strip, vocab_card, step_solver, voice_practice, magic_formula, did_you_know, text, canvas.`;

  const responseStream = await ai.models.generateContentStream({
    model: 'gemini-3-flash-preview',
    contents: `Create an interactive lesson about: ${payload.topic}. Force JSON format.`,
    config: {
      systemInstruction,
      responseMimeType: "application/json"
    }
  });

  for await (const chunk of responseStream) {
    if (chunk.text) {
      onChunk(chunk.text);
    }
  }
}
